import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  studiesApi, quotationsApi, companiesApi, priceListsApi, studyPricesApi, quoteFormatApi 
} from "@/lib/api";
import { toast } from "sonner";
import { 
  Calculator, Trash2, Search, X, Building2, FileText, Printer, Download 
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Quotation() {
  const [companies, setCompanies] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [studies, setStudies] = useState([]);
  const [studyPrices, setStudyPrices] = useState([]);
  const [studyPricesCache, setStudyPricesCache] = useState({});
  const [quoteConfig, setQuoteConfig] = useState(null);
  
  const [selectedCompany, setSelectedCompany] = useState("");
  const [activePriceListId, setActivePriceListId] = useState("");
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [formData, setFormData] = useState({
    patient_name: "",
    discount: 0,
    instructions: "",
    valid_days: 30,
  });
  
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Study search
  const [studySearchTerm, setStudySearchTerm] = useState("");
  const [showStudyDropdown, setShowStudyDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  
  // Refs for keyboard navigation
  const studySearchRef = useRef(null);
  const patientNameRef = useRef(null);
  const studyInputRef = useRef(null);
  const discountRef = useRef(null);
  const instructionsRef = useRef(null);

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (studySearchRef.current && !studySearchRef.current.contains(event.target)) {
        setShowStudyDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset highlighted index when search results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [studySearchTerm]);

  const fetchData = async () => {
    try {
      const [companiesRes, studiesRes, priceListsRes, quoteConfigRes] = await Promise.all([
        companiesApi.getAll(),
        studiesApi.getAll(),
        priceListsApi.getAll(),
        quoteFormatApi.get()
      ]);
      
      setCompanies(companiesRes.data);
      setStudies(studiesRes.data.filter(s => s.active));
      setQuoteConfig(quoteConfigRes.data);
      setPriceLists(priceListsRes.data);
      
      // Precargar precios de TODAS las listas en paralelo
      const allPriceLists = priceListsRes.data;
      const pricesPromises = allPriceLists.map(pl => 
        studyPricesApi.getAll(pl.id).then(res => ({ id: pl.id, prices: res.data }))
      );
      const allPrices = await Promise.all(pricesPromises);
      
      const cache = {};
      for (const { id, prices } of allPrices) {
        cache[id] = prices;
      }
      setStudyPricesCache(cache);
      
      // Set default price list
      const defaultList = allPriceLists.find(p => p.is_default);
      if (defaultList) {
        setActivePriceListId(defaultList.id);
        setStudyPrices(cache[defaultList.id] || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Error al cargar datos");
    }
  };

  const fetchStudyPrices = async (priceListId) => {
    if (!priceListId) return;
    // Usar caché si ya existe
    if (studyPricesCache[priceListId]) {
      setStudyPrices(studyPricesCache[priceListId]);
      return;
    }
    try {
      const response = await studyPricesApi.getAll(priceListId);
      setStudyPrices(response.data);
      setStudyPricesCache(prev => ({ ...prev, [priceListId]: response.data }));
    } catch (error) {
      console.error("Error fetching study prices:", error);
    }
  };

  // Handle price list selection (previously company)
  const handlePriceListChange = async (priceListId) => {
    if (!priceListId || priceListId === "none") {
      setSelectedCompany("");
      setActivePriceListId("");
      return;
    }
    
    const priceList = priceLists.find(p => p.id === priceListId);
    if (priceList) {
      setSelectedCompany(priceListId); // Store price list ID
      setActivePriceListId(priceListId);
      await fetchStudyPrices(priceListId);
      setShowQuotationModal(true);
      // Focus patient name after modal opens
      setTimeout(() => patientNameRef.current?.focus(), 100);
    }
  };

  // Get price for a study from current price list
  const getStudyPrice = (studyId) => {
    const priceEntry = studyPrices.find(p => p.study_id === studyId);
    if (priceEntry) return priceEntry.price;
    const study = studies.find(s => s.id === studyId);
    return study?.base_price || study?.price || 0;
  };

  // Filter studies for search
  const filteredStudies = studies.filter(study => {
    if (!studySearchTerm) return false;
    const searchLower = studySearchTerm.toLowerCase();
    const nameMatch = study.name?.toLowerCase().includes(searchLower);
    const codeMatch = study.code?.toLowerCase().includes(searchLower);
    const alreadyAdded = selectedStudies.some(s => s.study_id === study.id);
    return (nameMatch || codeMatch) && !alreadyAdded;
  });

  // Add study
  const addStudy = (study) => {
    const price = getStudyPrice(study.id);
    
    const newStudy = {
      study_id: study.id,
      study_name: study.name,
      study_code: study.code || "",
      price: price
    };
    
    setSelectedStudies(prev => [...prev, newStudy]);
    setStudySearchTerm("");
    setShowStudyDropdown(false);
    setHighlightedIndex(0);
    toast.success(`${study.name} agregado - $${price}`);
    
    // Keep focus on search input for adding more studies
    setTimeout(() => studyInputRef.current?.focus(), 50);
  };

  // Remove study
  const removeStudy = (studyId) => {
    const study = selectedStudies.find(s => s.study_id === studyId);
    setSelectedStudies(prev => prev.filter(s => s.study_id !== studyId));
    if (study) {
      toast.info(`${study.study_name} eliminado`);
    }
  };

  // Handle keyboard navigation in study search
  const handleStudySearchKeyDown = (e) => {
    if (!showStudyDropdown || filteredStudies.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Move to discount field
        discountRef.current?.focus();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredStudies.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredStudies.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredStudies[highlightedIndex]) {
          addStudy(filteredStudies[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowStudyDropdown(false);
        break;
      default:
        break;
    }
  };

  // Handle Enter key to move to next field
  const handlePatientNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      studyInputRef.current?.focus();
    }
  };

  const handleDiscountKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      instructionsRef.current?.focus();
    }
  };

  const handleInstructionsKeyDown = (e) => {
    // Allow Enter for new lines in textarea, but Ctrl+Enter to generate
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      if (selectedStudies.length > 0) {
        handleGenerate('print');
      }
    }
  };

  // Calculate totals
  const subtotal = selectedStudies.reduce((sum, s) => sum + (s.price || 0), 0);
  const discountAmount = formData.discount || 0;
  const total = Math.max(0, subtotal - discountAmount);

  // Generate quotation
  const handleGenerate = async (action) => {
    if (selectedStudies.length === 0) {
      toast.error("Agregue al menos un estudio");
      return;
    }

    setGenerating(true);
    try {
      const priceList = priceLists.find(p => p.id === selectedCompany);
      
      const quotationData = {
        patient_name: formData.patient_name || "Paciente",
        company_id: selectedCompany || null,
        company_name: priceList?.name || "PARTICULAR",
        studies: selectedStudies,
        profiles: [],
        subtotal,
        discount: discountAmount,
        total,
        notes: formData.instructions || null,
        valid_days: formData.valid_days || 30,
        price_list_id: activePriceListId
      };

      await quotationsApi.create(quotationData);
      
      const printContent = generatePrintContent(quotationData);
      
      if (action === 'print') {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.onload = () => {
            printWindow.print();
          };
        }
        toast.success("Cotización generada - Imprimiendo...");
      } else {
        const blob = new Blob([printContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cotizacion_${new Date().toISOString().split('T')[0]}.html`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success("Cotización descargada");
      }

      setSelectedStudies([]);
      setFormData({ patient_name: "", discount: 0, instructions: "", valid_days: 30 });
      setShowQuotationModal(false);
      setSelectedCompany("");
      
    } catch (error) {
      toast.error("Error al generar cotización");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  // Generate print content
  const generatePrintContent = (data) => {
    const config = quoteConfig || {};
    const headerColor = config.header_color || '#333333';
    
    const today = new Date().toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
    
    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + (data.valid_days || 30));
    const validUntilStr = validUntil.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Build header info
    const labName = config.lab_name || 'Laboratorio Clínico';
    const labAddress = config.lab_address || '';
    const labPhone = config.lab_phone || '';
    const labEmail = config.lab_email || '';
    const labRfc = config.lab_rfc || '';
    const labWebsite = config.lab_website || '';
    const logoHeader = config.logo_header || '';
    const footerText = config.footer_text || '';
    const termsText = config.terms_text || '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cotización - ${data.company_name}</title>
        <style>
          @page { margin: 15mm; }
          body { font-family: Arial, sans-serif; padding: 15px 30px; max-width: 700px; margin: 0 auto; font-size: 12px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid ${headerColor}; padding-bottom: 15px; }
          .header h1 { margin: 0; color: ${headerColor}; font-size: 20px; }
          .header p { margin: 3px 0; color: #666; font-size: 10px; }
          .header .lab-name { font-size: 12px; font-weight: bold; margin: 3px 0; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 15px; gap: 10px; }
          .info-box { background: #f5f5f5; padding: 8px 12px; border-radius: 5px; flex: 1; }
          .info-box label { font-weight: bold; color: #666; font-size: 10px; display: block; }
          .info-box p { margin: 3px 0 0; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th { background: ${headerColor}; color: white; padding: 8px; text-align: left; font-size: 11px; }
          td { padding: 8px; border-bottom: 1px solid #ddd; font-size: 11px; }
          .totals { margin-top: 15px; text-align: right; }
          .totals .row { display: flex; justify-content: flex-end; gap: 30px; padding: 3px 0; font-size: 12px; }
          .totals .total { font-size: 14px; font-weight: bold; border-top: 2px solid ${headerColor}; padding-top: 8px; }
          .instructions { margin-top: 20px; padding: 10px; background: #fffbeb; border-left: 4px solid #f59e0b; }
          .instructions h3 { margin: 0 0 8px; color: #92400e; font-size: 12px; }
          .footer { margin-top: 25px; text-align: center; font-size: 10px; color: #666; }
          .terms { margin-top: 15px; font-size: 9px; color: #888; white-space: pre-line; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          ${logoHeader ? `<img src="${logoHeader}" alt="Logo" />` : ''}
          <h1>COTIZACIÓN</h1>
          <p class="lab-name">${labName}</p>
          ${labRfc ? `<p>RFC: ${labRfc}</p>` : ''}
          ${labAddress ? `<p>${labAddress}</p>` : ''}
          ${(labPhone || labEmail) ? `<p>${[labPhone ? `Tel: ${labPhone}` : '', labEmail].filter(Boolean).join(' | ')}</p>` : ''}
          ${labWebsite ? `<p>${labWebsite}</p>` : ''}
        </div>
        
        <div class="info-row">
          <div class="info-box">
            <label>EMPRESA</label>
            <p>${data.company_name}</p>
          </div>
          <div class="info-box">
            <label>FECHA</label>
            <p>${today}</p>
          </div>
          <div class="info-box">
            <label>VIGENCIA</label>
            <p>Hasta ${validUntilStr}</p>
          </div>
        </div>
        
        ${data.patient_name ? `
        <div class="info-box" style="margin-bottom: 20px;">
          <label>PACIENTE</label>
          <p>${data.patient_name}</p>
        </div>
        ` : ''}
        
        <table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Estudio</th>
              <th style="text-align: right;">Precio</th>
            </tr>
          </thead>
          <tbody>
            ${data.studies.map(s => `
              <tr>
                <td>${s.study_code || '-'}</td>
                <td>${s.study_name}</td>
                <td style="text-align: right;">$${s.price.toLocaleString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="row">
            <span>Subtotal:</span>
            <span>$${data.subtotal.toLocaleString()}</span>
          </div>
          ${data.discount > 0 ? `
          <div class="row" style="color: green;">
            <span>Descuento:</span>
            <span>-$${data.discount.toLocaleString()}</span>
          </div>
          ` : ''}
          <div class="row total">
            <span>TOTAL:</span>
            <span>$${data.total.toLocaleString()}</span>
          </div>
        </div>
        
        ${data.notes ? `
        <div class="instructions">
          <h3>Indicaciones al Paciente</h3>
          <p>${data.notes}</p>
        </div>
        ` : ''}
        
        <div class="footer">
          ${footerText ? `<p>${footerText}</p>` : `<p>Esta cotización tiene una vigencia de ${data.valid_days || 30} días a partir de la fecha de emisión.</p>`}
          ${termsText ? `<div class="terms">${termsText}</div>` : '<p>Los precios pueden variar sin previo aviso.</p>'}
        </div>
      </body>
      </html>
    `;
  };

  // Get selected price list name for display
  const getSelectedPriceListName = () => {
    if (!selectedCompany || selectedCompany === "none") return "";
    const priceList = priceLists.find(p => p.id === selectedCompany);
    return priceList?.name || "";
  };

  return (
    <div className="space-y-6" data-testid="quotation-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Cotización
        </h1>
        <p className="text-muted-foreground">
          Seleccione una empresa para generar una cotización
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5" />
            Nueva Cotización
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Label className="text-sm font-medium flex items-center gap-2 mb-2">
              <Building2 className="h-4 w-4" />
              Empresa
            </Label>
            <Select 
              value={selectedCompany || ""} 
              onValueChange={handlePriceListChange}
            >
              <SelectTrigger data-testid="select-company">
                <SelectValue placeholder="Seleccionar empresa..." />
              </SelectTrigger>
              <SelectContent>
                {priceLists.map(priceList => (
                  <SelectItem key={priceList.id} value={priceList.id}>
                    {priceList.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              Seleccione una lista de precios para comenzar la cotización
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Quotation Modal */}
      <Dialog open={showQuotationModal} onOpenChange={(open) => {
        setShowQuotationModal(open);
        if (!open) {
          // Resetear la selección de empresa al cerrar el modal
          setSelectedCompany("");
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Cotización - {getSelectedPriceListName()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Patient Name */}
            <div className="space-y-2">
              <Label>Nombre del Paciente (opcional)</Label>
              <Input
                ref={patientNameRef}
                placeholder="Nombre del paciente..."
                value={formData.patient_name}
                onChange={(e) => setFormData(prev => ({ ...prev, patient_name: e.target.value }))}
                onKeyDown={handlePatientNameKeyDown}
                data-testid="quotation-patient-name"
              />
              <p className="text-xs text-muted-foreground">Presiona Enter para ir a buscar estudios</p>
            </div>

            {/* Study Search */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Estudios</Label>
                {activePriceListId && (
                  <span className="text-xs text-muted-foreground">
                    Lista: {priceLists.find(p => p.id === activePriceListId)?.name || "Default"}
                  </span>
                )}
              </div>
              
              <div className="relative" ref={studySearchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={studyInputRef}
                  placeholder="Buscar estudio por nombre o código... (Enter para seleccionar)"
                  value={studySearchTerm}
                  onChange={(e) => {
                    setStudySearchTerm(e.target.value);
                    setShowStudyDropdown(e.target.value.length > 0);
                  }}
                  onFocus={() => studySearchTerm && setShowStudyDropdown(true)}
                  onKeyDown={handleStudySearchKeyDown}
                  className="pl-10"
                  data-testid="search-study"
                />
                {studySearchTerm && (
                  <button 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setStudySearchTerm("");
                      setShowStudyDropdown(false);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                
                {/* Search Results Dropdown */}
                {showStudyDropdown && filteredStudies.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredStudies.map((study, index) => {
                      const price = getStudyPrice(study.id);
                      return (
                        <button
                          key={study.id}
                          className={`w-full px-4 py-2 text-left flex justify-between items-center ${
                            index === highlightedIndex 
                              ? 'bg-primary text-primary-foreground' 
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => addStudy(study)}
                          onMouseEnter={() => setHighlightedIndex(index)}
                        >
                          <span>
                            {study.code && <span className={`font-mono mr-2 ${index === highlightedIndex ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{study.code}</span>}
                            {study.name}
                          </span>
                          <span className={`font-semibold ${index === highlightedIndex ? 'text-primary-foreground' : 'text-green-600'}`}>${price}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {showStudyDropdown && studySearchTerm && filteredStudies.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                    No se encontraron estudios
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Usa ↑↓ para navegar, Enter para seleccionar, Esc para cerrar
              </p>

              {/* Studies Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Código</TableHead>
                      <TableHead>Estudio</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedStudies.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          Busque y agregue estudios a la cotización
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedStudies.map((study, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono">{study.study_code || "-"}</TableCell>
                          <TableCell>{study.study_name}</TableCell>
                          <TableCell className="text-right font-medium">${study.price?.toLocaleString() || 0}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => removeStudy(study.study_id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Discount */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Descuento ($)</Label>
                <Input
                  ref={discountRef}
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.discount || ""}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    discount: parseFloat(e.target.value) || 0 
                  }))}
                  onKeyDown={handleDiscountKeyDown}
                  data-testid="quotation-discount"
                />
                <p className="text-xs text-muted-foreground">Presiona Enter para ir a indicaciones</p>
              </div>
              <div className="space-y-2">
                <Label>Vigencia (días)</Label>
                <Input 
                  type="number"
                  min="1"
                  value={formData.valid_days}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    valid_days: parseInt(e.target.value) || 30 
                  }))}
                  data-testid="quotation-valid-days"
                />
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-2">
              <Label>Indicaciones al Paciente</Label>
              <Textarea
                ref={instructionsRef}
                placeholder="Ej: Ayuno de 8 a 12 horas, no fumar, evitar ejercicio intenso..."
                value={formData.instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                onKeyDown={handleInstructionsKeyDown}
                rows={3}
                data-testid="quotation-instructions"
              />
              <p className="text-xs text-muted-foreground">Ctrl+Enter para generar e imprimir</p>
            </div>

            {/* Totals */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal ({selectedStudies.length} estudio{selectedStudies.length !== 1 ? 's' : ''}):</span>
                <span>${subtotal.toLocaleString()}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Descuento:</span>
                  <span>-${discountAmount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span className="text-green-600">${total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <Button variant="outline" onClick={() => setShowQuotationModal(false)}>
              Cancelar
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => handleGenerate('download')}
                disabled={generating || selectedStudies.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar
              </Button>
              <Button 
                onClick={() => handleGenerate('print')}
                disabled={generating || selectedStudies.length === 0}
                data-testid="generate-quotation-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                {generating ? "Generando..." : "Generar e Imprimir"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
